import React from 'react';
import { usePpsAccess } from 'hooks/usePpsAccess';

interface AccessControlProps {
  fieldName: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  isEditMode?: boolean;
}

/**
 * A wrapper component that controls access to form fields based on RBAC rules
 */
export const FieldAccessControl: React.FC<AccessControlProps> = ({
  fieldName,
  children,
  fallback,
  isEditMode = false
}) => {
  const { isFieldDisabled, isLoading } = usePpsAccess();

  // If permissions are still loading, render a placeholder or the children
  if (isLoading) {
    return <>{children}</>;
  }

  // Check if the field should be disabled
  const disabled = isFieldDisabled(fieldName);

  // If disabled and there's a fallback, use it
  if (disabled && fallback) {
    return <>{fallback}</>;
  }

  // Clone child element and add disabled property
  const child = React.Children.only(children) as React.ReactElement;
  return React.cloneElement(child, {
    disabled: disabled || child.props.disabled
  });
};

interface PermissionGuardProps {
  permission: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

/**
 * A component that renders children only if user has specified permission
 */
export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  permission,
  children,
  fallback = null
}) => {
  const { hasPermission, isLoading } = usePpsAccess();

  // If permissions are still loading, don't render anything
  if (isLoading) {
    return null;
  }

  return hasPermission(permission) ? <>{children}</> : <>{fallback}</>;
};
